import { type ShareableResource } from "@/contexts/ShareResourceContext";
import { getPrincipal } from "@/principal";
import { useGetAccount } from "@/queries/account-queries";
import { useListGroups } from "@/queries/group-queries";
import { useCreateProjectPolicy, useDeleteProjectPolicy, useProjectState } from "@/queries/project-queries";
import { useListRunnerPolicies, useUpdateSharingSetting } from "@/queries/runner-queries";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import { ProjectRole } from "gitpod-next-api/gitpod/v1/project_pb";
import { useCallback, useMemo } from "react";

const GroupNames = {
    OrgMembers: "org-members",
};

type UseResourceSharingReturn = {
    isShared: boolean;
    share: () => Promise<void>;
    unshare: () => Promise<void>;
    isPending: boolean;
    orgName: string;
};

export const useResourceSharing = (resource: ShareableResource): UseResourceSharingReturn => {
    const { data: account } = useGetAccount();

    // Project-specific hooks
    const { data: projectState } = useProjectState(
        resource.resourceType === ResourceType.PROJECT ? resource.resourceId : undefined,
    );
    const createProjectPolicy = useCreateProjectPolicy();
    const deleteProjectPolicy = useDeleteProjectPolicy();

    // Runner-specific hooks
    const { data: runnerPolicies } = useListRunnerPolicies(
        resource.resourceType === ResourceType.RUNNER ? resource.resourceId : "",
    );
    const { data: groups } = useListGroups();
    const updateSharingSetting = useUpdateSharingSetting(
        resource.resourceType === ResourceType.RUNNER ? resource.resourceId : "",
    );

    const principal = getPrincipal();
    const membership = principal ? account?.memberships?.find((m) => m.userId === principal) : undefined;
    const orgName = membership?.organizationName || "your Organization";

    // Compute org-members group
    const orgMembersGroup = useMemo(() => {
        if (!membership || !groups) {
            return undefined;
        }
        return groups.groups.find(
            (g) => g.organizationId === membership.organizationId && g.name === GroupNames.OrgMembers,
        );
    }, [membership, groups]);

    // Compute isShared based on resource type
    const isShared = useMemo(() => {
        if (resource.resourceType === ResourceType.PROJECT) {
            return projectState?.shared ?? false;
        } else if (resource.resourceType === ResourceType.RUNNER) {
            if (!runnerPolicies || !orgMembersGroup) {
                return false;
            }
            return runnerPolicies.policies.some((p) => p.groupId === orgMembersGroup.id);
        }
        return false;
    }, [resource.resourceType, projectState, runnerPolicies, orgMembersGroup]);

    // Share action
    const share = useCallback(async (): Promise<void> => {
        if (resource.resourceType === ResourceType.PROJECT) {
            if (!projectState) {
                throw new Error("Project state not loaded");
            }
            await createProjectPolicy.mutateAsync({
                projectId: resource.resourceId,
                groupId: projectState.orgMembersGroupId,
                role: ProjectRole.USER,
            });
        } else if (resource.resourceType === ResourceType.RUNNER) {
            if (!orgMembersGroup) {
                throw new Error("Organization members group not found");
            }
            await updateSharingSetting.mutateAsync({
                setting: "anyone-in-org",
                groupId: orgMembersGroup.id,
            });
        }
    }, [resource, projectState, orgMembersGroup, createProjectPolicy, updateSharingSetting]);

    // Unshare action
    const unshare = useCallback(async (): Promise<void> => {
        if (resource.resourceType === ResourceType.PROJECT) {
            if (!projectState) {
                throw new Error("Project state not loaded");
            }
            await deleteProjectPolicy.mutateAsync({
                projectId: resource.resourceId,
                groupId: projectState.orgMembersGroupId,
            });
        } else if (resource.resourceType === ResourceType.RUNNER) {
            if (!orgMembersGroup) {
                throw new Error("Organization members group not found");
            }
            await updateSharingSetting.mutateAsync({
                setting: "only-me",
                groupId: orgMembersGroup.id,
            });
        }
    }, [resource, projectState, orgMembersGroup, deleteProjectPolicy, updateSharingSetting]);

    const isPending =
        resource.resourceType === ResourceType.PROJECT
            ? createProjectPolicy.isPending || deleteProjectPolicy.isPending
            : updateSharingSetting.isPending;

    return {
        isShared,
        share,
        unshare,
        isPending,
        orgName,
    };
};
